import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { confirmPasswordReset, verifyPasswordResetCode, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../utils/FireBaseConfigurations/config";
import { theme } from "../../theme";
import TrueID_logo_white from "../../assests/TrueID-logo-white.png";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState("");

  const oobCode = searchParams.get("oobCode");
  const mode = searchParams.get("mode");

  useEffect(() => {
    // Verify the reset code is valid
    const verifyCode = async () => {
      if (!oobCode || mode !== "resetPassword") {
        setError("Invalid or expired password reset link.");
        setVerifying(false);
        return;
      }

      try {
        // Verify the password reset code and get the email
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setVerifying(false);
      } catch (err) {
        console.error("Error verifying reset code:", err);
        if (err.code === "auth/invalid-action-code") {
          setError(
            "This password reset link has expired or has already been used."
          );
        } else {
          setError("Invalid password reset link.");
        }
        setVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!newPassword) {
      setError("Please enter a new password");
      return;
    }

    if (!confirmPassword) {
      setError("Please confirm your password");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Password strength validation - check all requirements at once
    const hasMinLength = newPassword.length >= 8;
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasDigit = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasMinLength || !hasUppercase || !hasDigit || !hasSpecialChar) {
      setError("Password must be at least 8 characters with 1 uppercase, 1 digit, and 1 special character");
      return;
    }

    setLoading(true);

    try {
      // Check if new password is same as old password
      try {
        await signInWithEmailAndPassword(auth, email, newPassword);
        // If login succeeds, it means the password is the same
        setError("New password cannot be the same as your previous password");
        setLoading(false);
        return;
      } catch (authError) {
        // If login fails, it's good - means password is different
        // Continue with password reset
      }

      // Reset the password using Firebase
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);

      // Redirect to home page after 3 seconds
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (err) {
      console.error("Password reset error:", err);

      if (err.code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password.");
      } else if (err.code === "auth/invalid-action-code") {
        setError(
          "This password reset link has expired or has already been used."
        );
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: {xs: "80%", sm: "60%", md: "40%", lg: "35%"},
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
	height:"100vh"
      }}
    >
      <Container maxWidth="sm">
	  <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <Box
            component="img"
            src={TrueID_logo_white}
            alt="TrueID Logo"
            sx={{
              height: "50px",
              padding: "0.5px",
              borderRadius: "10px",
            }}
          />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: "#f0f0f0",
            
          }}
        >

          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: theme.palette.primary.main,
              textAlign: "center",
              mb: 1,
            }}
          >
            Reset your password
          </Typography>

          {email && (
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                textAlign: "center",
                mb: 3,
              }}
            >
              for {email}
            </Typography>
          )}

          {!success ? (
            <>
              {!oobCode && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                    {error || "Invalid or expired password reset link."}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/")}
                    sx={{
                      backgroundColor: "#000",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#333" },
                    }}
                  >
                    Go to Home
                  </Button>
                </Box>
              )}

              {oobCode && (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    {/* New Password */}
                    <Grid size={12}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#464545", mb: 0.5 }}
                      >
                        New password
                      </Typography>
                      <TextField
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                        }}
                      />
                    </Grid>

                    {/* Confirm Password */}
                    <Grid size={12}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#464545", mb: 0.5 }}
                      >
                        Confirm password
                      </Typography>
                      <TextField
                        type={showConfirmPassword ? "text" : "password"}
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                edge="end"
                                size="small"
                              >
                                {showConfirmPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                        }}
                      />
                    </Grid>

                    {/* Error Message */}
                    {error && (
                      <Grid size={12}>
                        <Typography
                          variant="caption"
                          color="error"
                          textAlign="center"
                          display="block"
                        >
                          {error}
                        </Typography>
                      </Grid>
                    )}

                    {/* Submit Button */}
                    <Grid size={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading || !newPassword || !confirmPassword}
                        sx={{
                        //   backgroundColor: "#4052d4",
                          color: "#fff",
                          padding: "10px",
                          borderRadius: "10px",
                          textTransform: "uppercase",
                          fontWeight: "bold",
                        //   "&:hover": { backgroundColor: "#3041c0" },
                          "&.Mui-disabled": {
                            backgroundColor: "#ccc",
                            color: "#666",
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : (
                          "SAVE"
                        )}
                      </Button>
                    </Grid>

                    {/* Back to Sign In */}
                    <Grid size={12}>
                      <Button
                        variant="text"
                        fullWidth
                        onClick={() => navigate("/")}
                        sx={{
                          textTransform: "none",
                          textDecoration: "underline",
                        }}
                      >
                        Back to Sign In
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography
                variant="h6"
                sx={{ color: theme.palette.primary.main, mb: 2 }}
              >
                ✓ Password reset successful!
              </Typography>
              <Typography variant="body2" sx={{ color: "#464545", mb: 2 }}>
                Your password has been changed successfully.
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Redirecting to home page...
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;

